import React from 'react';
import { Button, Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { appColors } from '../../Config/Theme';

interface StatusActionButtonProps {
  label: string;
  icon: React.ReactElement;
  isActive: boolean;
  activeBg: string;
  activeBorder: string;
  activeColor: string;
  /** If true, use inverted styling (white text on colored bg) */
  inverted?: boolean;
  /** Optional tooltip / title text */
  title?: string;
  onClick: () => void;
}

const BUTTON_WIDTH = 144;
const BUTTON_HEIGHT = 44;
const ICON_SLOT_WIDTH = 20;

const StatusActionButton: React.FC<StatusActionButtonProps> = ({
  label,
  icon,
  isActive,
  activeBg,
  activeBorder,
  activeColor,
  inverted = false,
  title,
  onClick,
}) => {
  const glowColor = activeBorder;

  return (
    <Button
      size="small"
      onClick={onClick}
      title={title}
      aria-label={title || label}
      sx={{
        width: { xs: '100%', sm: BUTTON_WIDTH },
        minWidth: { xs: 0, sm: BUTTON_WIDTH },
        maxWidth: { xs: '100%', sm: BUTTON_WIDTH },
        height: BUTTON_HEIGHT,
        minHeight: BUTTON_HEIGHT,
        boxSizing: 'border-box',
        borderRadius: '8px',
        textTransform: 'none',
        whiteSpace: 'nowrap',
        px: 1.5,
        border: '2px solid',
        borderColor: isActive ? activeBorder : appColors.border.default,
        boxShadow: isActive ? `0 0 0 3px ${alpha(glowColor, 0.25)}` : 'none',
        bgcolor: isActive ? (inverted ? activeBorder : activeBg) : appColors.surface.card,
        color: isActive ? (inverted ? '#FFFFFF' : activeColor) : appColors.text.body,
        '&:hover': {
          bgcolor: isActive
            ? (inverted ? alpha(activeBorder, 0.85) : activeBg)
            : appColors.surface.inactive,
          borderColor: activeBorder,
        },
      }}
    >
      {isActive ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `${ICON_SLOT_WIDTH}px auto`,
            alignItems: 'center',
            gap: 0.5,
            width: '100%',
          }}
        >
          <Box sx={{ width: ICON_SLOT_WIDTH, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: inverted ? '#FFFFFF' : activeColor }}>
            {icon}
          </Box>
          <Typography
            component="span"
            sx={{
              fontSize: '0.8rem',
              fontWeight: 700,
              lineHeight: 1.2,
              textAlign: 'left',
            }}
          >
            {label}
          </Typography>
        </Box>
      ) : (
        <Typography
          component="span"
          sx={{
            fontSize: '0.8rem',
            fontWeight: 600,
            lineHeight: 1.2,
            textAlign: 'center',
          }}
        >
          {label}
        </Typography>
      )}
    </Button>
  );
};

export default StatusActionButton;
